'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Music2, CheckCircle, Calendar, Clock, User, Building2, Phone, Mail, MapPin, Play, Star } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const DEMO_INCLUDES = [
  'Live walkthrough of all 3 portals (Admin, Client, Vendor)',
  'White-label branding customization demo',
  'AI virtual assistant configuration',
  'Automated vendor dispatch workflow',
  'Contract e-signature and payment flow',
  'Q&A with our onboarding team',
];

const TIME_SLOTS = [
  { date: 'Mon, Aug 4', time: '10:00 AM CST', available: true },
  { date: 'Mon, Aug 4', time: '2:00 PM CST', available: true },
  { date: 'Tue, Aug 5', time: '11:00 AM CST', available: true },
  { date: 'Tue, Aug 5', time: '3:00 PM CST', available: false },
  { date: 'Wed, Aug 6', time: '10:00 AM CST', available: true },
  { date: 'Wed, Aug 6', time: '1:00 PM CST', available: true },
];

const SERVICES_OPTIONS = [
  'Mobile DJ', 'Karaoke Host', 'Bingo Night', 'Trivia Night',
  'Corporate Events', 'Weddings', 'Birthday Parties', 'Bar/Nightclub Events',
];

export default function WhiteLabelDemo() {
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [form, setForm] = useState({
    name: '', businessName: '', email: '', phone: '',
    city: '', state: '', eventsPerYear: '', message: '',
  });

  const handleChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleService = (s: string) => {
    setSelectedServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name && form.email && selectedSlot !== null) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="w-24 h-24 rounded-full gold-gradient flex items-center justify-center mx-auto mb-6 glow-gold">
            <CheckCircle size={44} className="text-black" />
          </div>
          <h1 className="text-3xl font-extrabold gold-gradient-text mb-4">Demo Booked! 🎉</h1>
          <p className="text-muted-foreground text-lg mb-2">
            We'll see you on <span className="text-foreground font-semibold">{TIME_SLOTS[selectedSlot!]?.date} at {TIME_SLOTS[selectedSlot!]?.time}</span>.
          </p>
          <p className="text-muted-foreground mb-8">A calendar invite and Zoom link will be sent to <span className="text-primary">{form.email}</span>.</p>
          <div className="space-y-3">
            <Link href="/white-label" className="block w-full py-3 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all">
              Back to Home
            </Link>
            <Link href="/white-label/pricing" className="block w-full py-3 rounded-xl border border-primary/40 text-primary font-semibold hover:bg-primary/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="light-burst w-96 h-96 bg-primary top-[-10%] right-[-5%]" />
        <div className="light-burst w-64 h-64 bg-accent bottom-[20%] left-[5%]" />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-16">
          <Link href="/white-label" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
              <Music2 size={13} className="text-black" />
            </div>
            <span className="font-bold text-sm gold-gradient-text">EventPro Suite</span>
          </div>
          <Link href="/white-label/onboarding" className="px-4 py-2 rounded-xl gold-gradient text-black font-semibold text-sm hover:opacity-90 transition-all">
            Start Free Trial
          </Link>
        </div>
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/30 bg-accent/5 text-accent text-xs font-mono mb-6">
            <Play size={12} /> LIVE DEMO — 30 MINUTES
          </div>
          <h1 className="text-5xl font-extrabold mb-4">
            <span className="gold-gradient-text">See the Platform</span>
            <br />
            <span className="text-foreground">in Action</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Book a free 30-minute live demo with our team. We'll walk you through every feature and answer your questions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* LEFT: Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="card-glass rounded-2xl p-6 neon-border-gold">
                <h2 className="font-bold text-lg mb-5">Your Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'name', label: 'Your Name *', placeholder: 'Marcus Thompson', icon: User },
                    { key: 'businessName', label: 'Business Name', placeholder: 'Platinum Sound Events', icon: Building2 },
                    { key: 'email', label: 'Email Address *', placeholder: 'dj@yourbusiness.com', icon: Mail },
                    { key: 'phone', label: 'Phone Number', placeholder: '(555) 000-0000', icon: Phone },
                    { key: 'city', label: 'City', placeholder: 'Atlanta', icon: MapPin },
                    { key: 'state', label: 'State', placeholder: 'GA', icon: MapPin },
                  ].map(field => {
                    const Icon = field.icon;
                    return (
                      <div key={field.key}>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{field.label}</label>
                        <div className="relative">
                          <Icon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input
                            type="text"
                            value={form[field.key as keyof typeof form]}
                            onChange={e => handleChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            className="w-full pl-8 pr-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Events Per Year */}
              <div className="card-glass rounded-2xl p-6 border border-border">
                <h2 className="font-bold text-lg mb-4">About Your Business</h2>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">How many events per year?</label>
                  <select
                    value={form.eventsPerYear}
                    onChange={e => handleChange('eventsPerYear', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground focus:outline-none focus:border-primary text-sm"
                  >
                    <option value="">Select range</option>
                    <option value="1-25">1–25 events</option>
                    <option value="26-50">26–50 events</option>
                    <option value="51-100">51–100 events</option>
                    <option value="100+">100+ events</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">What services do you offer?</label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICES_OPTIONS.map(svc => (
                      <button
                        key={svc}
                        type="button"
                        onClick={() => toggleService(svc)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${selectedServices.includes(svc) ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-border/80'}`}
                      >
                        {svc}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="card-glass rounded-2xl p-6 border border-border">
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Anything specific you'd like to see?</label>
                <textarea
                  value={form.message}
                  onChange={e => handleChange('message', e.target.value)}
                  placeholder="e.g. I want to see how the vendor dispatch works, or how clients sign contracts..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm resize-none"
                />
              </div>

              {/* Time Slots */}
              <div className="card-glass rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar size={16} className="text-primary" />
                  <h2 className="font-bold text-lg">Pick a Time Slot *</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {TIME_SLOTS.map((slot, i) => (
                    <button
                      key={i}
                      type="button"
                      disabled={!slot.available}
                      onClick={() => setSelectedSlot(i)}
                      className={`p-3 rounded-xl border text-left transition-all ${!slot.available ? 'opacity-30 cursor-not-allowed border-border' : selectedSlot === i ? 'border-primary/60 bg-primary/10' : 'border-border hover:border-primary/40'}`}
                    >
                      <p className={`text-xs font-bold mb-0.5 ${selectedSlot === i ? 'text-primary' : 'text-foreground'}`}>{slot.date}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock size={10} /> {slot.time}
                      </p>
                      {!slot.available && <p className="text-[10px] text-red-400 mt-1">Booked</p>}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={!form.name || !form.email || selectedSlot === null}
                className="w-full py-4 rounded-xl gold-gradient text-black font-bold text-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Calendar size={18} /> Book My Demo
              </button>
            </form>
          </div>

          {/* RIGHT: Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* What's Included */}
            <div className="card-glass rounded-2xl p-6 neon-border-gold">
              <h3 className="font-bold text-lg mb-5">What's Included</h3>
              <ul className="space-y-3">
                {DEMO_INCLUDES.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle size={15} className="text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="card-glass rounded-2xl p-6 border border-border">
              <h3 className="font-bold text-lg mb-4">Prefer to reach out directly?</h3>
              <div className="space-y-3">
                <a href="mailto:sales@eventprosuite.com" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <Mail size={15} className="text-primary" />
                  sales@eventprosuite.com
                </a>
                <a href="tel:+18005550000" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <Phone size={15} className="text-primary" />
                  1-800-555-0000
                </a>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Clock size={15} className="text-primary" />
                  Mon–Fri, 9am–6pm CST
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div className="card-glass rounded-2xl p-6 border border-border">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-primary text-primary" />)}
              </div>
              <p className="text-sm text-muted-foreground italic mb-3">"The demo sold me in 15 minutes. I signed up before the call ended."</p>
              <p className="text-xs font-semibold text-foreground">— DJ Destiny Rose, Houston TX</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
